import React, { FC, KeyboardEvent, useRef, useState, useEffect, MouseEvent, FocusEvent, ChangeEvent } from "react"
import Icon, { ICON_TYPES } from "@components/Icon/Icon"

import { updateJobTitleField } from "../../../../_store/actions"
import type { DispatchType } from "../../../../_store/types"

import { JobTitleFieldDIV } from "./JobTitleField.styles"

type JobTitleFieldProps = {
  value: string
  dispatch: DispatchType
}

enum FIELD_STATES {
  FOCUSED = "FOCUSED",
  SUBMITTED = "SUBMITTED",
  BLURRED = "BLURRED",
}

const FIELD_STATE_DATA: { [k in FIELD_STATES]: { className: string, icon?: keyof typeof ICON_TYPES } } = {
  [FIELD_STATES.FOCUSED]: {
    className: "is-focused",
    icon: "pencil",
  },
  [FIELD_STATES.SUBMITTED]: {
    className: "is-submitted",
    icon: "checkmark",
  },
  [FIELD_STATES.BLURRED]: {
    className: "is-blurred",
    icon: "pencil",
  },
}

const PLACEHOLDER_TEXT: string = "Add a job title"

const JobTitleField: FC<JobTitleFieldProps> = ({
  value,
  dispatch,
}) => {
  /* local state for input field */
  const [_value, _setValue] = useState(value || "")
  const [fieldState, setFieldState] = useState<FIELD_STATES>(
    value ? FIELD_STATES.BLURRED : FIELD_STATES.FOCUSED
  )
  const inputEl = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Update local field state if global state changes
  useEffect(() => {
    _setValue(value)
  }, [value])

  useEffect(() => {
    if (fieldState === FIELD_STATES.BLURRED && inputEl.current) {
      inputEl.current.blur()
    }
    if (fieldState === FIELD_STATES.FOCUSED && inputEl.current) {
      // When the field is focused, set the cursor to the end of the value
      setTimeout(() => {
        if (inputEl.current) inputEl.current.setSelectionRange(9999, 9999)
      }, 0)
    }
  }, [fieldState])

  // Save title on enter keypress
  const handleEnterKeyPress = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      saveTitle(_value)
    }
  }

  const saveTitle = (title: string): void => {
    if (title?.length > 0) {
      setFieldState(FIELD_STATES.SUBMITTED)
      dispatch(updateJobTitleField(title))
      // Blur the field after title is saved
      setTimeout(() => setFieldState(FIELD_STATES.BLURRED), 250)
    }
  }

  return (
    <JobTitleFieldDIV
      className={`${FIELD_STATE_DATA[fieldState].className} ${!_value ? "is-placeholder" : ""}`}
      ref={wrapperRef}
    >
      {FIELD_STATE_DATA[fieldState].icon && (
        <button onClick={() => {
          if (!_value || fieldState === FIELD_STATES.BLURRED) {
            if (inputEl.current) inputEl.current.focus()
            return
          }
          if (fieldState === FIELD_STATES.FOCUSED) {
            if (inputEl.current) inputEl.current.blur()
          }
        }}>
          <Icon
            size="20px"
            type={ICON_TYPES[FIELD_STATE_DATA[fieldState].icon || ICON_TYPES["close"]]}
          />
        </button>
      )}
      <div className="field-wrapper">
        <input
          className="type--text-medium-M margin-bottom--0"
          maxLength={50}
          ref={inputEl}
          type="text"
          onFocus={(): void => setFieldState(FIELD_STATES.FOCUSED)}
          onBlur={(e: FocusEvent<HTMLInputElement>): void => saveTitle(e.target.value)}
          onChange={(e: ChangeEvent<HTMLInputElement>): void => _setValue(e.target.value)}
          onKeyUp={handleEnterKeyPress}
          value={_value}
          placeholder={PLACEHOLDER_TEXT}
        />
        {/* Shadow component to render the bottom-border as the width of the text */}
        <span className="type--text-medium-M margin-bottom--0">{_value || PLACEHOLDER_TEXT}</span>
      </div>
    </JobTitleFieldDIV>
  )
}

export default JobTitleField
